import { msalInstance, loginRequest } from './msalConfig';
import { invalidateFetchCache } from './utils/fetchCache';

const API_BASE = 'https://fa-nordcomply-prod-sc-hbbzewhdgbe0h6de.swedencentral-01.azurewebsites.net';

// The server's own browser reads a report with a short-lived token instead of a login (see pages/ReportRender.jsx).
// It lives in memory only, never in localStorage.
let renderToken = null;
export function setRenderToken(token) {
  renderToken = token || null;
}

// MSAL 5 looks an account up by a filter (the older getAccountByHomeId is gone, and calling it threw, so no renewal ever ran).
function findAccount(stored) {
  return msalInstance.getAccount({ homeAccountId: stored.id }) || msalInstance.getAllAccounts()[0] || null;
}

function getStoredUser() {
  const stored = localStorage.getItem('nordcomply_user');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Graph access tokens expire after roughly an hour — without renewal, any
// apiFetch after that window 401s and force-logs the user out mid-session,
// on whatever page they happen to be on (not specific to any one page or
// action, just whichever request first hits the stale token). MSAL caches
// the underlying refresh token in localStorage (msalConfig's cacheLocation)
// and can silently mint a fresh access token from it with no user
// interaction, as long as the Microsoft SSO session itself is still valid.
async function getFreshToken() {
  if (renderToken) return renderToken;
  const stored = getStoredUser();
  if (!stored) return null;
  // A local (email/password + TOTP) session token is opaque and server-
  // side only — there's no MSAL account behind it to silently refresh.
  // apiFetch's existing 401 handling (clear + redirect to /login) already
  // covers expiry correctly once the backend rejects it.
  if (stored.authMethod === 'local') return stored.token;
  try {
    const account = findAccount(stored);
    if (!account) return stored.token;
    const result = await msalInstance.acquireTokenSilent({ scopes: loginRequest.scopes, account });
    if (result.accessToken !== stored.token) {
      localStorage.setItem('nordcomply_user', JSON.stringify({ ...stored, token: result.accessToken }));
    }
    return result.accessToken;
  } catch {
    // Silent renewal failed (e.g. the underlying SSO session itself expired)
    // — fall back to the old token and let the 401 handling below take
    // over, rather than blocking the request entirely.
    return stored.token;
  }
}

// Why a person was last signed out (and the last renewal that failed), kept on the device so the reason can be read afterwards: the
// login page shows a short code, and the details are in localStorage under nc-signout. Nothing here is sent anywhere.
let lastRenewalError = null;
function errInfo(err) {
  return { code: (err && (err.errorCode || err.name)) || null, msg: String((err && err.message) || '').slice(0, 160) };
}
function noteSignout(step, extra = {}) {
  try {
    localStorage.setItem('nc-signout', JSON.stringify({ at: new Date().toISOString(), step, ...extra, lastRenewal: lastRenewalError }));
  } catch {
    // private mode: the reason is simply not kept
  }
}
export function readSignoutReason() {
  try { return JSON.parse(localStorage.getItem('nc-signout') || 'null'); } catch { return null; }
}

// Renewal while the person is working: asks for a new access token when the old one is close to the end (MSAL hands back the
// kept one while it still has time), so an hour of work never ends on a stale token. A failure is only noted; the next request
// does what it always did (renew by force, then a quiet trip through Microsoft).
export async function renewTokenQuietly() {
  if (renderToken) return;
  const stored = getStoredUser();
  if (!stored || stored.authMethod === 'local') return;
  try {
    const account = findAccount(stored);
    if (!account) return;
    const result = await msalInstance.acquireTokenSilent({ scopes: loginRequest.scopes, account });
    if (result.accessToken !== stored.token) localStorage.setItem('nordcomply_user', JSON.stringify({ ...stored, token: result.accessToken }));
    lastRenewalError = null;
  } catch (err) {
    lastRenewalError = { at: new Date().toISOString(), ...errInfo(err) };
  }
}

// One more chance before a person is signed out: a new access token is asked for by force (the old one may have gone stale
// without the page noticing), and only when that fails too is the session over.
async function forceNewToken() {
  const stored = getStoredUser();
  if (!stored || stored.authMethod === 'local') return null;
  try {
    const account = findAccount(stored);
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({ scopes: loginRequest.scopes, account, forceRefresh: true });
    localStorage.setItem('nordcomply_user', JSON.stringify({ ...stored, token: result.accessToken }));
    return result.accessToken;
  } catch (err) {
    lastRenewalError = { at: new Date().toISOString(), ...errInfo(err) };
    return null;
  }
}

// A quiet new sign-in through Microsoft (no account picker), back to the same page. Once per five minutes, so it can never loop.
let reauthWhy = 'reauth-failed';
async function reauthenticate() {
  const stored = getStoredUser();
  if (!stored || stored.authMethod === 'local') { reauthWhy = 'local-session'; return false; }
  try {
    const last = Number(sessionStorage.getItem('nc-reauth-at') || 0);
    if (Date.now() - last < 5 * 60 * 1000) { reauthWhy = 'reauth-tried-minutes-ago'; return false; }
    sessionStorage.setItem('nc-reauth-at', String(Date.now()));
    const account = findAccount(stored);
    await msalInstance.acquireTokenRedirect({ scopes: loginRequest.scopes, account, loginHint: stored.email });
    return true;
  } catch (err) {
    reauthWhy = 'reauth-error';
    lastRenewalError = { at: new Date().toISOString(), ...errInfo(err) };
    return false;
  }
}

export async function apiFetch(path, options = {}) {
  const token = await getFreshToken();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && !renderToken) {
    const renewed = await forceNewToken();
    if (renewed) res = await fetch(`${API_BASE}${path}`, { ...options, headers: { ...headers, Authorization: `Bearer ${renewed}` } });
  }

  if (res.status === 403 && !renderToken) {
    const info = await res.clone().json().catch(() => null);
    if (info && info.code === 'account_locked') {
      localStorage.removeItem('nordcomply_user');
      window.location.href = '/login?locked=1';
      throw new Error('Kontoen er låst');
    }
  }

  if (res.status === 401 && !renderToken) {
    // Before signing anyone out: Microsoft's own session is usually still alive, so a quick trip there and back renews the sign-in.
    if (await reauthenticate()) return new Promise(() => {});
    noteSignout(reauthWhy, { path: path.split('?')[0] });
    localStorage.removeItem('nordcomply_user');
    window.location.href = '/login?expired=1';
    throw new Error('Session udløbet - log ind igen');
  }

  // A successful write makes every cached copy of that resource stale.
  if (res.ok && (options.method || 'GET').toUpperCase() !== 'GET') invalidateFetchCache(path);

  return res;
}

// Triggers a fresh Graph pull for one tenant, refreshing the backend's
// data cache (see azure-function/src/lib/app.js withCache/handleSync) —
// everything else reads from that cache instead of calling Graph live.
// The backend throttles this to at most once per hour per tenant unless
// force is set — pass force only for an explicit user-initiated "Sync now"
// action, never for an automatic/background call.
export async function syncTenant(gid, { force = false } = {}) {
  const res = await apiFetch(`/api/sync?tenantId=${gid}${force ? '&force=1' : ''}`, { method: 'POST' });
  return res.json();
}

// Feedback to NordComply support (logged-in users only), see azure-function/src/lib/app.js handleFeedback.
export async function sendFeedback({ type, message, email }) {
  const res = await apiFetch('/api/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, message, email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke sende feedback');
  return data;
}

// Organizations (MSPs) — provider-only, see azure-function/src/lib/app.js
// handleCreateOrganization/handleListOrganizations/handleDeleteOrganization.
export async function listOrganizations() {
  const res = await apiFetch('/api/organizations');
  return res.json();
}

export async function createOrganization(fields) {
  const res = await apiFetch('/api/organizations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return res.json();
}

export async function deleteOrganization(id) {
  const res = await apiFetch(`/api/organizations?id=${id}`, { method: 'DELETE' });
  return res.json();
}

export async function updateOrganization(id, fields) {
  const res = await apiFetch('/api/organizations', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...fields }),
  });
  return res.json();
}

// Audit notes — free-text context attached to a page, pulled into the
// Audit & Rapportering report. See azure-function/src/lib/app.js
// handleListAuditNotes/handleCreateAuditNote/handleDeleteAuditNote.
export async function listAuditNotes(tenantId) {
  const res = await apiFetch(`/api/audit-notes?tenantId=${tenantId}`);
  return res.json();
}

export async function createAuditNote(tenantId, page, pageLabel, text, { itemKey, findingKey, accepted, title, status, reviewDate, decisionType, owner, ownerEmail } = {}) {
  const res = await apiFetch('/api/audit-notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, page, pageLabel, text, itemKey, findingKey, accepted, title, status, reviewDate, decisionType, owner, ownerEmail }),
  });
  return res.json();
}

export async function deleteAuditNote(tenantId, id) {
  const res = await apiFetch(`/api/audit-notes?tenantId=${tenantId}&id=${id}`, { method: 'DELETE' });
  return res.json();
}

export async function updateAuditNote(tenantId, id, { text, decisionType, status, reviewDate, owner, ownerEmail } = {}) {
  const res = await apiFetch('/api/audit-notes', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, id, text, decisionType, status, reviewDate, owner, ownerEmail }),
  });
  return res.json();
}

// Automatic report emails — "Planlæg automatisk afsendelse" on Revision &
// rapporter. See azure-function/src/lib/app.js
// handleGetReportSchedule/handleSaveReportSchedule/checkAllScheduledReports.
export async function getReportSchedule(tenantId) {
  const res = await apiFetch(`/api/report-schedule?tenantId=${tenantId}`);
  return res.json();
}

export async function saveReportSchedule(tenantId, schedule) {
  const res = await apiFetch('/api/report-schedule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, ...schedule }),
  });
  return res.json();
}

// Sends the tenant's SAVED schedule right now, as a test — same code path as the real automatic
// send, so a successful call here is real proof it works. Requires a schedule with at least one
// recipient to already be saved (via saveReportSchedule) — it does not take ad hoc recipients.
export async function sendReportNow(tenantId) {
  const res = await apiFetch('/api/report-schedule/send-now', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId }),
  });
  return res.json();
}

// Local (email/password + TOTP) auth — see azure-function/src/lib/app.js
// handleGetInvite/handleCompleteSetup/handleLocalLogin/handleLocalLogout.
// All three setup/login calls run before there's any session, so apiFetch's
// "no stored user yet" case (no Authorization header) is exactly right here.
export async function getInvite(token) {
  const res = await apiFetch(`/api/invite?token=${encodeURIComponent(token)}`);
  return res.json();
}

// fields: { password, currentPassword, totpCode } — which ones are needed
// depends on the link's mode (setup / password reset / MFA reset).
export async function completeSetup(token, fields) {
  const res = await apiFetch('/api/complete-setup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...fields }),
  });
  return res.json();
}

// The activity log (NordComply staff only). `group`: logins | users | tenants | organizations; `after`: the previous page's `next`.
export async function getActivityLog({ group = '', after = null, limit = 100, orgId = '', tenantId = '', from = '', to = '' } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (group) params.set('group', group);
  if (after) params.set('after', after);
  if (orgId) params.set('orgId', orgId);
  if (tenantId) params.set('tenantId', tenantId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const res = await apiFetch(`/api/activity-log?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke hente aktivitetsloggen');
  return data;
}

// Nyheder: everyone signed in reads them; NordComply (with the permission) writes them.
export async function getNews() {
  const res = await apiFetch('/api/news');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke hente nyheder');
  return data;
}
export async function markNewsRead() {
  await apiFetch('/api/news/read', { method: 'POST' }).catch(() => {});
}
export async function getNewsAdmin() {
  const res = await apiFetch('/api/news/admin');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke hente nyheder');
  return data;
}
export async function saveNews(method, payload) {
  const res = await apiFetch('/api/news', { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke gemme nyheden');
  return data;
}
export async function deleteNews(id) {
  const res = await apiFetch(`/api/news?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke slette nyheden');
  return data;
}
export async function getSupportDocs() {
  const res = await apiFetch('/api/support-docs');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke hente vejledningerne');
  return data.items || [];
}
export async function saveSupportDoc(doc) {
  const res = await apiFetch('/api/support-docs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(doc) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke gemme vejledningen');
  return data;
}
export async function deleteSupportDoc(id) {
  const res = await apiFetch(`/api/support-docs?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke slette vejledningen');
  return data;
}
export async function getLicensesOverview(month = '') {
  const res = await apiFetch(`/api/licenses-overview${month ? `?month=${month}` : ''}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke hente licenserne');
  return data;
}

// Locks (locked true) or unlocks an account: a locked person is turned away at once, nothing is deleted.
export async function lockUser(email, locked) {
  const res = await apiFetch('/api/users/lock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, locked }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke ændre kontoens lås');
  return data;
}

// Activates an account that is waiting for approval (a new account cannot sign in before that).
export async function approveUser(email) {
  const res = await apiFetch('/api/users/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke aktivere brugeren');
  return data;
}

// Admin/provider: reset a local user's password, MFA or both. The user gets a
// one-time link by email; the admin never sees a password or a secret.
export async function resetUserCredentials(email, reset) {
  const res = await apiFetch('/api/users/reset-credentials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, reset }),
  });
  return res.json();
}

// Sends the welcome mail again to someone who has never signed in. A password account gets a new one-time link.
export async function resendWelcomeMail(email) {
  const res = await apiFetch('/api/users/resend-welcome', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function localLogin(email, password, totpCode) {
  // Not apiFetch: nobody is signed in yet, so a 401 here is a wrong answer on the form, not an expired session.
  const res = await fetch(`${API_BASE}/api/local-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, totpCode }),
  });
  return res.json();
}

export async function localLogout() {
  const res = await apiFetch('/api/local-logout', { method: 'POST' });
  return res.json();
}

export { API_BASE };
// A person's own mail notifications about new findings (Notifikationer in the account menu).
export async function getNotificationPrefs() {
  const res = await apiFetch('/api/notification-prefs');
  return res.json();
}

export async function saveNotificationPrefs({ frequency, types, tenantIds }) {
  const res = await apiFetch('/api/notification-prefs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ frequency, types, tenantIds }),
  });
  return res.json();
}
